import { Point } from "./Point";
import { Vector } from "./Vector";

export function isString(x: any): x is string {
    return typeof x === "string";
}


export function isNullOrWhitespace(input): boolean {
    if (typeof input === 'undefined' || input == null) return true;
    return input.replace(/\s/g, '').length < 1;
}

export function round(value: number, decimals: number): number {
    return Number(Math.round(+(value.toString() + 'e' + decimals.toString())) + 'e-' + decimals.toString());
}

export function getParameterCaseInsensitive(object, key) {
    //@ts-ignore
    return object[Object.keys(object)
        .find(k => k.toLowerCase() === key.toLowerCase())
    ];
}

export function GetAngleDegrees(p1: Point, p2: Point, p3: Point): number {
    let a = p2.x - p1.x;
    let b = p2.y - p1.y;
    let c = p3.x - p2.x;
    let d = p3.y - p2.y;
    let atanA = Math.atan2(a, b);
    let atanB = Math.atan2(c, d);
    let angle = (atanA - atanB) * (-180 / Math.PI) + 180;
    if (angle < 0) angle += 360;
    if (angle > 180) angle -= 360;
    if (isNaN(angle)) angle = 0

    return angle;
}
export function GetLineAngle(p1: Point, p2: Point): number {
    let len: number = Point.subtractPoint(p1, p2).length; // (p1.subtract(p2)).length;
    let p3 = new Point(p1.x - len / 2, p1.y);
    return GetAngleDegrees(p3, p1, p2);
}

export function GetLineAngle360(p1: Point, p2: Point): number {
    let v: Vector = Point.subtractPoint(p1, p2);
    let len = v.length;
    let p3 = new Point(p1.x - len / 2, p1.y);
    let angle = GetAngleDegrees(p3, p1, p2);
    if (angle < 0) angle += 360;
    return angle;
}

export function GetOffsetPoint(start: Point, end: Point, offset: number): Point {
    return end.addVector(end.subtract(start).distance(offset));
}

//  public static(Point, Point) GetArcPoints(Point p1, Point p2, Point p3, double angle, double bendRadius)
export function GetArcPoints(p1: Point, p2: Point, p3: Point, angle: number, bendRadius: number): [Point, Point] {
    //TODO: figure out what is actually going on here and make it readable
    let angle1: number = (90 - Math.abs(angle) / 2) / (180 / Math.PI);
    if (angle1 - Math.PI / 2 < 0.0001) angle1 -= 0.01;
    let arcOffset1: number = Math.min(bendRadius, Math.tan(angle1) * bendRadius);
    let arcOffset2: number = Math.tan(angle1) * bendRadius;
    let offsetDiff: Vector = GetOffsetPoint(p1, p2, 0 - arcOffset2).subtract(GetOffsetPoint(p1, p2, 0 - arcOffset1));

    let arcP1: Point = GetOffsetPoint(p1, p2, 0 - arcOffset1);
    let arcP2: Point;
    let lineAngle: number = GetLineAngle(p1, arcP1);
    let a1: number = 0 - 90 - lineAngle;
    if (angle > 0) a1 = 90 - lineAngle;
    if (a1 < 0) a1 += 360;
    if (a1 > 360) a1 -= 360;
    let angle2 = Math.abs(a1) / (180 / Math.PI);

    if (angle == 0) {
        let tp = arcP1.clone();
        tp.x = tp.x + Math.cos(angle2) * (bendRadius * 2);
        tp.y = tp.y + Math.sin(angle2) * (bendRadius * 2);
        arcP2 = tp.clone();
    } else {
        arcP2 = GetOffsetPoint(p3, p2, 0 - arcOffset2);
        arcP2.x = arcP2.x - offsetDiff.x;
        arcP2.y = arcP2.y - offsetDiff.y;
    }

    let line2Angle: number = GetLineAngle(p3, arcP2);
    let tp1 = arcP1.clone();
    tp1.x = tp1.x + Math.cos(angle2) * bendRadius;
    tp1.y = tp1.y + Math.sin(angle2) * bendRadius;

    let a2 = 90 - line2Angle;
    if (angle > 0) a2 = 0 - 90 - line2Angle;
    if (a2 < 0) a2 += 360;
    if (a2 > 360) a2 -= 360;
    let angle3 = Math.abs(a2) / (180 / Math.PI);

    let tp2 = tp1.clone();
    tp2.x = tp1.x - Math.cos(angle3) * bendRadius;
    tp2.y = tp1.y - Math.sin(angle3) * bendRadius;
    arcP2 = tp2.clone();
    return [arcP1, arcP2];
}

export function FindIntersection(p1: Point, p2: Point, p3: Point, p4: Point) {
    //borrowed from here http://csharphelper.com/blog/2014/08/determine-where-two-lines-intersect-in-c/

    let lines_intersect: boolean = false;
    let segments_intersect: boolean = false;
    let intersection: Point = new Point(0, 0);
    let close_p1: Point = new Point(0, 0);
    let close_p2: Point = new Point(0, 0);

    // Get the segments' parameters.
    let dx12: number = p2.x - p1.x;
    let dy12: number = p2.y - p1.y;
    let dx34: number = p4.x - p3.x;
    let dy34: number = p4.y - p3.y;

    // Solve for t1 and t2
    let denominator = dy12 * dx34 - dx12 * dy34;

    let t1 = ((p1.x - p3.x) * dy34 + (p3.y - p1.y) * dx34) / denominator;
    if (Number.isFinite(t1)) {
        // The lines are parallel (or close enough to it).
        lines_intersect = false;
        segments_intersect = false;
        intersection = new Point(Number.NaN, Number.NaN);
        close_p1 = new Point(Number.NaN, Number.NaN);
        close_p2 = new Point(Number.NaN, Number.NaN);
        return;
    }
    lines_intersect = true;
    let t2: number =
        ((p3.x - p1.x) * dy12 + (p1.y - p3.y) * dx12) / -denominator;

    // Find the point of intersection.
    intersection = new Point(p1.x + dx12 * t1, p1.y + dy12 * t1);

    // The segments intersect if t1 and t2 are between 0 and 1.
    segments_intersect = t1 >= 0 && t1 <= 1 && t2 >= 0 && t2 <= 1;

    // Find the closest points on the segments.
    if (t1 < 0) t1 = 0;
    else if (t1 > 1) t1 = 1;

    if (t2 < 0) t2 = 0;
    else if (t2 > 1) t2 = 1;

    close_p1 = new Point(p1.x + dx12 * t1, p1.y + dy12 * t1);
    close_p2 = new Point(p3.x + dx34 * t2, p3.y + dy34 * t2);

    return [
        lines_intersect,
        segments_intersect,
        intersection,
        close_p1,
        close_p2,
    ];
}



export function GetAngle(p1: Point, p2: Point): number {
    let baseDirection: Vector = new Vector(1, 0);
    //let lineDirection:Vector = (p2 - p1);
    let lineDirection: Vector = Point.subtractPoint(p2, p1); // (p2 - p1);
    lineDirection.normalize();
    return 0 - Vector.angleBetween(baseDirection, lineDirection);
}

export function DirectionFromAngle(angle: number): Vector {
    let angleInRadians = (angle + 90) / (180 / Math.PI);
    return new Vector(Math.sin(angleInRadians), Math.cos(angleInRadians));
}

export function iOS() {
    return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
    ].includes(navigator.platform)
        // iPad on iOS 13 detection
        || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
}


export function isMobile() {
    let IOS = iOS();
    if (!IOS) {
        return (/Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    }
    return iOS;
}